import { useAppSelector } from "integrations/redux/hooks"
import {
    DownloadButtonData,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import {
    ColorFinderState,
    selectVideoColorFinderJobByFlightId,
    selectVideoColorFinderUploads,
} from "./ColorFinderSlice"
import { selectVideoColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { createFlightpathKML } from "@nova/data-create-flight-path-kml"
import { stageKml } from "features/modals/ExportLayers/module"
import { downloadUriSameOrigin } from "tools/browser/download"
import { useVideoMetadata } from "./useVideoMetadata"
import { Licenses, JobStatusType } from "@nova/constants"
import { createS3DownloadUrlInBrowser } from "plugins/shared/s3url"

export const useVideoDownloadButtonData = (): DownloadButtonData => {
    const selectedLayerId = useAppSelector(selectSelectedLayerIds).at(-1)
    const id = selectedLayerId || ""
    const job = useAppSelector(selectVideoColorFinderJobByFlightId(id))
    const flight = useAppSelector(selectVideoColorFinderFlightById(id))
    const uploads = useAppSelector(selectVideoColorFinderUploads(flight?.id || ""))

    const { videoMetadata } = useVideoMetadata(id)

    const jobIsCompleted = job?.status === JobStatusType.COMPLETED

    const mp4Blob = (
        uploads?.find((upload) => upload.id.toLowerCase().endsWith(".mp4")) as ColorFinderState
    )?.file
    const srtBlob = (
        uploads?.find((upload) => upload.id.toLowerCase().endsWith(".srt")) as ColorFinderState
    )?.file

    const downloadButtonData: NestedDownloadData = {}

    const videoOptions: NestedDownloadData = {}

    if (jobIsCompleted || (mp4Blob && (job?.status || 0) >= JobStatusType.STARTED)) {
        const downloadVideo = async () => {
            if (!!mp4Blob) {
                const a = document.createElement("a")
                const url = URL.createObjectURL(mp4Blob)
                a.href = url
                a.download = mp4Blob.name
                a.click()
                URL.revokeObjectURL(url)
            } else if (job && !("mock" in job)) {
                const s3Key = !!flight?.demoId
                    ? `public/sample-data/${job.videoPath}`
                    : job.videoPath
                const url = await createS3DownloadUrlInBrowser(s3Key, !!flight?.demoId)
                window.open(url, "_blank")
            }
        }

        videoOptions.MP4 = { callback: downloadVideo, restrictedLicenses: [Licenses.Lite] }
    }

    const flightPathOptions: NestedDownloadData = {}

    if (jobIsCompleted || (srtBlob && (job?.status || 0) >= JobStatusType.STARTED)) {
        const downloadSRT = async () => {
            if (!!srtBlob) {
                const a = document.createElement("a")
                const url = URL.createObjectURL(srtBlob)
                a.href = url
                a.download = srtBlob.name
                a.click()
                URL.revokeObjectURL(url)
            } else if (job && !("mock" in job) && job.srtPath) {
                const s3Key = !!flight?.demoId ? `public/sample-data/${job.srtPath}` : job.srtPath
                const url = await createS3DownloadUrlInBrowser(s3Key, !!flight?.demoId)
                window.open(url, "_blank")
            }
        }

        flightPathOptions.SRT = { callback: downloadSRT, restrictedLicenses: [Licenses.Lite] }
    }

    if (videoMetadata && flight) {
        const downloadFlightPathKML = async () => {
            const flightpath = videoMetadata?.map((frame) => ({
                longitude: frame[0],
                latitude: frame[1],
                altitude: frame[2],
            }))
            const kml = createFlightpathKML(flight.name + " Flightpath", [flightpath], false)

            const files: { blob: Blob; name: string }[] = []
            stageKml(kml, flight.name, files)

            const url = URL.createObjectURL(files[0].blob)
            downloadUriSameOrigin(files[0].name, url)
        }

        flightPathOptions.KML = {
            callback: downloadFlightPathKML,
            restrictedLicenses: [Licenses.Lite],
        }
    }

    if (Object.keys(videoOptions).length > 0) downloadButtonData.Video = videoOptions
    if (Object.keys(flightPathOptions).length > 0)
        downloadButtonData["Flight Path"] = flightPathOptions

    return { exportOptions: downloadButtonData }
}
